import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { attendanceAPI, subscriptionsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Info, User, Baby, Users, CalendarDays } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '../../components/ui/popover';

const WEEKDAYS = ['Lu', 'Ma', 'Mi', 'Jo', 'Vi', 'Sâ', 'Du'];
const MONTHS = [
  'Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie',
  'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie',
];

// Culorile membrilor familiei (titular / partener / copii)
const personStyle = (type) => {
  if (type === 'user') return { chip: 'bg-[#6db025] text-black', icon: User };
  if (type === 'secondary_parent') return { chip: 'bg-blue-500 text-white', icon: Users };
  return { chip: 'bg-purple-500 text-white', icon: Baby };
};

const LOW_SESSIONS_THRESHOLD = 2;

const lowSubMessage = (name) =>
  `Abonamentul lui ${name} se apropie de final (mai are cel mult ${LOW_SESSIONS_THRESHOLD} ședințe). ` +
  'Te rugăm să îl contactezi pe Ovidiu sau recepția bazinului Fiald pentru reînnoire.';

const UserCalendarPage = () => {
  const { user } = useAuth();
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth()); // 0-11
  const [attendance, setAttendance] = useState([]);
  const [lowByPerson, setLowByPerson] = useState({}); // person_id -> true dacă abonamentul activ e aproape de final
  const [activeSubByPerson, setActiveSubByPerson] = useState({}); // person_id -> abonament activ
  const [loading, setLoading] = useState(true);

  // Membrii familiei
  const persons = useMemo(() => {
    if (!user) return [];
    const list = [{ id: user.id, type: 'user', name: user.name }];
    if (user.secondary_parent_id && user.secondary_parent_name) {
      list.push({ id: user.secondary_parent_id, type: 'secondary_parent', name: user.secondary_parent_name });
    }
    (user.children || []).forEach((c) => list.push({ id: c.id, type: 'child', name: c.name }));
    return list;
  }, [user]);

  // Prezențele lunii afișate
  const fetchAttendance = useCallback(async () => {
    setLoading(true);
    try {
      const start = new Date(viewYear, viewMonth, 1);
      const end = new Date(viewYear, viewMonth + 1, 0, 23, 59, 59);
      const res = await attendanceAPI.getMine({
        start_date: start.toISOString(),
        end_date: end.toISOString(),
      });
      setAttendance(res.data);
    } catch (error) {
      toast.error('Eroare la încărcarea prezențelor');
    } finally {
      setLoading(false);
    }
  }, [viewYear, viewMonth]);

  useEffect(() => {
    fetchAttendance();
  }, [fetchAttendance]);

  // Statusul abonamentelor active (pentru iconița de informare)
  useEffect(() => {
    if (!persons.length) return;
    let cancelled = false;
    (async () => {
      const low = {};
      const active = {};
      for (const person of persons) {
        try {
          const res = await subscriptionsAPI.getForPerson(person.id, person.type);
          const activeSub = res.data.find((s) => s.status === 'active');
          if (activeSub) {
            active[person.id] = activeSub;
            if (activeSub.sessions_total && activeSub.sessions_remaining <= LOW_SESSIONS_THRESHOLD) {
              low[person.id] = true;
            }
          }
        } catch {
          /* membru fără abonamente - ignorăm */
        }
      }
      if (!cancelled) {
        setLowByPerson(low);
        setActiveSubByPerson(active);
      }
    })();
    return () => { cancelled = true; };
  }, [persons]);

  // Grupăm prezențele pe zi: { 'YYYY-M-D': [{person_id, person_name, person_type}] }
  const entriesByDay = useMemo(() => {
    const map = {};
    attendance.forEach((att) => {
      const d = new Date(att.recorded_at);
      if (d.getFullYear() !== viewYear || d.getMonth() !== viewMonth) return;
      const key = d.getDate();
      if (!map[key]) map[key] = [];
      // O singură apariție per persoană per zi
      if (!map[key].some((e) => e.person_id === att.person_id)) {
        map[key].push({ person_id: att.person_id, person_name: att.person_name, person_type: att.person_type });
      }
    });
    return map;
  }, [attendance, viewYear, viewMonth]);

  // Grila calendarului (săptămâna începe luni)
  const cells = useMemo(() => {
    const firstDay = new Date(viewYear, viewMonth, 1);
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const leadingBlanks = (firstDay.getDay() + 6) % 7; // Duminică=0 → Luni=0
    const list = Array.from({ length: leadingBlanks }, () => null);
    for (let d = 1; d <= daysInMonth; d++) list.push(d);
    return list;
  }, [viewYear, viewMonth]);

  const changeMonth = (delta) => {
    const d = new Date(viewYear, viewMonth + delta, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  };

  const isToday = (day) =>
    day === today.getDate() && viewMonth === today.getMonth() && viewYear === today.getFullYear();

  return (
    <div className="space-y-6">
      {/* Header + navigare lună */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white uppercase tracking-tight">Calendar</h1>
          <p className="text-white/40 text-sm mt-1">Prezențele familiei și situația abonamentelor</p>
        </div>
        <div className="flex items-center gap-2 bg-[#0A0A0A] border border-white/5 rounded-sm">
          <button onClick={() => changeMonth(-1)} className="p-3 text-white/40 hover:text-[#6db025] transition-colors" aria-label="Luna anterioară">
            <ChevronLeft size={18} />
          </button>
          <span className="text-white font-heading font-bold uppercase tracking-widest text-sm min-w-[160px] text-center">
            {MONTHS[viewMonth]} {viewYear}
          </span>
          <button onClick={() => changeMonth(1)} className="p-3 text-white/40 hover:text-[#6db025] transition-colors" aria-label="Luna următoare">
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      {/* Status abonamente per membru */}
      <div className="flex flex-wrap gap-3">
        {persons.map((person) => {
          const style = personStyle(person.type);
          const Icon = style.icon;
          const sub = activeSubByPerson[person.id];
          const isLow = lowByPerson[person.id];
          if (!sub) return null;
          return (
            <div key={person.id} className={`flex items-center gap-3 px-4 py-3 border rounded-sm ${
              isLow ? 'bg-yellow-500/5 border-yellow-500/30' : 'bg-[#0A0A0A] border-white/5'
            }`}>
              <div className={`w-8 h-8 flex items-center justify-center rounded-sm ${style.chip}`}>
                <Icon size={15} />
              </div>
              <div>
                <p className="text-white text-xs font-bold uppercase tracking-widest">{person.name}</p>
                <p className={`text-[10px] font-black uppercase tracking-widest ${isLow ? 'text-yellow-400' : 'text-white/40'}`}>
                  {sub.sessions_total
                    ? `${sub.sessions_remaining} din ${sub.sessions_total} ședințe rămase`
                    : 'Abonament activ'}
                </p>
              </div>
              {isLow && (
                <Popover>
                  <PopoverTrigger asChild>
                    <button className="text-yellow-400 hover:text-yellow-300 transition-colors" aria-label="Detalii abonament">
                      <Info size={17} />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent side="top" className="bg-[#121212] border-yellow-500/30 text-white text-xs leading-relaxed w-72">
                    {lowSubMessage(person.name)}
                  </PopoverContent>
                </Popover>
              )}
            </div>
          );
        })}
      </div>

      {/* Grila calendarului */}
      <div className="bg-[#0A0A0A] border border-white/5">
        <div className="grid grid-cols-7 border-b border-white/5">
          {WEEKDAYS.map((day) => (
            <div key={day} className="py-3 text-center text-white/30 text-[10px] font-black uppercase tracking-widest">
              {day}
            </div>
          ))}
        </div>

        {loading ? (
          <div className="py-20 text-center">
            <div className="animate-pulse text-[#6db025] font-heading uppercase tracking-widest text-xs">Se încarcă...</div>
          </div>
        ) : (
          <div className="grid grid-cols-7">
            {cells.map((day, i) => {
              const entries = day ? entriesByDay[day] || [] : [];
              const hasEntries = entries.length > 0;
              return (
                <div
                  key={i}
                  className={`min-h-[68px] sm:min-h-[88px] p-1.5 sm:p-2 border-b border-r border-white/5 ${
                    !day ? 'bg-black/20' : hasEntries ? 'bg-[#6db025]/10' : ''
                  } ${isToday(day) ? 'ring-1 ring-inset ring-[#6db025]/60' : ''}`}
                >
                  {day && (
                    <>
                      <span className={`text-[11px] sm:text-xs font-bold ${
                        hasEntries ? 'text-[#6db025]' : isToday(day) ? 'text-white' : 'text-white/30'
                      }`}>
                        {day}
                      </span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {entries.map((entry) => {
                          const style = personStyle(entry.person_type);
                          const isLow = lowByPerson[entry.person_id];
                          const initial = (entry.person_name || '?').charAt(0).toUpperCase();
                          const chip = (
                            <span className={`relative w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center rounded-full text-[10px] sm:text-[11px] font-black ${style.chip}`}>
                              {initial}
                              {isLow && (
                                <span className="absolute -top-1.5 -right-1.5 w-3.5 h-3.5 bg-yellow-400 text-black rounded-full flex items-center justify-center">
                                  <Info size={9} strokeWidth={3} />
                                </span>
                              )}
                            </span>
                          );
                          // Doar chip-urile cu abonament aproape de final deschid mesajul de informare
                          return isLow ? (
                            <Popover key={entry.person_id}>
                              <PopoverTrigger asChild>
                                <button title={entry.person_name} aria-label={`Detalii ${entry.person_name}`}>{chip}</button>
                              </PopoverTrigger>
                              <PopoverContent side="top" className="bg-[#121212] border-yellow-500/30 text-white text-xs leading-relaxed w-72">
                                {lowSubMessage(entry.person_name)}
                              </PopoverContent>
                            </Popover>
                          ) : (
                            <span key={entry.person_id} title={entry.person_name}>{chip}</span>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Legendă */}
      <div className="bg-white/[0.02] border border-white/5 p-4 flex flex-wrap items-center gap-x-6 gap-y-2">
        <span className="flex items-center gap-2 text-white/40 text-[10px] font-black uppercase tracking-widest">
          <span className="w-4 h-4 bg-[#6db025]/20 border border-[#6db025]/40 rounded-sm" /> Zi cu prezență
        </span>
        <span className="flex items-center gap-2 text-white/40 text-[10px] font-black uppercase tracking-widest">
          <span className="w-4 h-4 bg-yellow-400 text-black rounded-full flex items-center justify-center"><Info size={10} strokeWidth={3} /></span>
          Abonament aproape de final - apasă pentru detalii
        </span>
        <span className="flex items-center gap-2 text-white/40 text-[10px] font-black uppercase tracking-widest">
          <CalendarDays size={14} className="text-[#6db025]" /> Chenar verde = astăzi
        </span>
      </div>
    </div>
  );
};

export default UserCalendarPage;
